import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import html2canvas from 'html2canvas';
import './VisitingCard.css';

const themes = [
  { bg: ['#1e3a5f', '#2d5a8e'],   accent: '#f59e0b', layout: 'left'  },
  { bg: ['#6d28d9', '#7c3aed'],   accent: '#a78bfa', layout: 'right' },
  { bg: ['#059669', '#10b981'],   accent: '#6ee7b7', layout: 'left'  },
  { bg: ['#7f1d1d', '#991b1b'],   accent: '#fca5a5', layout: 'left'  },
  { bg: ['#f87171', '#fb923c'],   accent: '#fff',    layout: 'right' },
  { bg: ['#1e40af', '#2563eb'],   accent: '#93c5fd', layout: 'left'  },
  { bg: ['#c2410c', '#ea580c'],   accent: '#fed7aa', layout: 'right' },
  { bg: ['#1e3a5f', '#1e40af'],   accent: '#93c5fd', layout: 'left'  },
  { bg: ['#0e7490', '#06b6d4'],   accent: '#a5f3fc', layout: 'right' },
  { bg: ['#db2777', '#ec4899'],   accent: '#fbcfe8', layout: 'left'  },
];

export default function VisitingCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [profileImg, setProfileImg] = useState(null);
  const [profile, setProfile] = useState(null);
  const fileRef = useRef();
  const cardRef = useRef();
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/api/users/profile').then(res => {
      const u = res.data.data;
      setProfile(u);
      if (u.avatar) setProfileImg(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${u.avatar}`);
    }).catch(() => setProfile(user));
  }, []);

  const p = profile || user || {};
  const theme = themes[selectedIdx];
  const name = p.name || 'Your Name';
  const occupation = p.occupation || 'Your Designation';
  const phone = p.phone || '0000000000';
  const email = p.email || 'email@example.com';
  const address = p.address || 'Your City, State';

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { useCORS: true, scale: 2, backgroundColor: null });
      const link = document.createElement('a');
      link.download = `visiting-card-${name}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch { alert('Download failed. Try again.'); }
    finally { setDownloading(false); }
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { useCORS: true, scale: 2, backgroundColor: null });
      canvas.toBlob(async (blob) => {
        if (navigator.share && blob) {
          const file = new File([blob], 'visiting-card.png', { type: 'image/png' });
          await navigator.share({ title: `${name} - Visiting Card`, files: [file] }).catch(() => {});
        } else {
          const link = document.createElement('a');
          link.download = 'visiting-card.png';
          link.href = canvas.toDataURL();
          link.click();
        }
      });
    } catch { alert('Share failed. Try again.'); }
  };

  const isRight = theme.layout === 'right';

  return (
    <div className="vc-page">
      {/* Header */}
      <div className="vc-header">
        <h2>Choose Your Business Card Design</h2>
      </div>

      <div className="vc-body container">
        {/* Color Swatches */}
        <div className="vc-swatches">
          {themes.map((t, i) => (
            <div
              key={i}
              className={`vc-swatch ${selectedIdx === i ? 'active' : ''}`}
              style={{ background: `linear-gradient(135deg, ${t.bg[0]}, ${t.bg[1]})` }}
              onClick={() => setSelectedIdx(i)}
            />
          ))}
        </div>

        {/* Card Preview */}
        <div className="vc-card-wrap">
          <div
            className="vc-card"
            ref={cardRef}
            style={{ background: `linear-gradient(135deg, ${theme.bg[0]}, ${theme.bg[1]})` }}
          >
            {/* Decorative circles */}
            <div className="vc-circle c1" />
            <div className="vc-circle c2" />

            {/* Top section */}
            <div className={`vc-top ${isRight ? 'reverse' : ''}`}>
              {!isRight && (
                <div className="vc-text-block">
                  <h2 className="vc-name">{name.toUpperCase()}</h2>
                  <p className="vc-occupation" style={{ color: theme.accent }}>{occupation}</p>
                </div>
              )}
              {/* Avatar */}
              <div className="vc-avatar-wrap" onClick={() => fileRef.current.click()}>
                {profileImg
                  ? <img src={profileImg} alt="profile" className="vc-avatar-img" />
                  : <div className="vc-avatar-placeholder">
                      <span>📷</span>
                    </div>
                }
                <input ref={fileRef} type="file" accept="image/*" hidden
                  onChange={e => { const f = e.target.files[0]; if (f) setProfileImg(URL.createObjectURL(f)); }} />
              </div>
              {isRight && (
                <div className="vc-text-block right">
                  <h2 className="vc-name">{name.toUpperCase()}</h2>
                  <p className="vc-occupation" style={{ color: theme.accent }}>{occupation}</p>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="vc-divider" />

            {/* Contact Info */}
            <div className="vc-contacts">
              <div className="vc-contact-row">
                <span className="vc-contact-icon" style={{ color: theme.accent }}>📞</span>
                <span>{phone}</span>
              </div>
              <div className="vc-contact-row">
                <span className="vc-contact-icon" style={{ color: theme.accent }}>✉️</span>
                <span>{email}</span>
              </div>
            </div>

            <div className="vc-divider" />

            <div className="vc-contact-row" style={{ marginTop: 8 }}>
              <span className="vc-contact-icon" style={{ color: theme.accent }}>📍</span>
              <span>{address}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="vc-actions">
          <button className="vc-action-btn" onClick={handleDownload} disabled={downloading} title="Download">
            {downloading ? '⏳' : '⬇️'}
          </button>
          <button className="vc-action-btn" onClick={handleShare} title="Share">
            📤
          </button>
        </div>

        {!user?.name && (
          <p className="vc-notice">
            ℹ️ <button onClick={() => navigate('/create-profile')}>Complete your profile</button> to see your details on the card.
          </p>
        )}
      </div>
    </div>
  );
}
